import { useCallback, useState } from 'react';

import InfiniteLoadingTrigger from '@cartwhl/ui/atoms/InfiniteLoadingTrigger';
import Text from '@cartwhl/ui/base/TextNew';
import ChevronLeftIcon from '@cartwhl/ui/icons/ChevronLeftIcon';
import LearnIcon from '@cartwhl/ui/icons/LearnIcon';
import TrashIcon from '@cartwhl/ui/icons/TrashIcon';

import { findAssetForGroupID } from '../assetLibraryAssetLifecycle';
import { getBookmarkReferenceAssets } from '../assetLibraryAssetsLoad';
import {
  ASSET_LIBRARY_LOAD_MORE_ROOT_MARGIN,
  getAssetLibraryBodyClassName,
  getAssetLibraryGridClassName,
} from '../assetLibraryGridLayout';
import { isGroupBookmarked } from '../assetLibraryGroupIds';
import { useStore } from '../assetLibraryStore';
import { useAssetLibraryAssetActions } from '../hooks/useAssetLibraryAssetActions';
import { isMainLibraryPageItem } from '../utils';
import AssetLibraryGroupItemCard from './AssetLibraryGroupItemCard';
import AssetLibraryLoading from './AssetLibraryLoading';
import AssetLibraryBlockedActionTooltip from './AssetLibraryTrashActionTooltip';

const GROUP_MOVE_TO_TRASH_BUTTON_CLASS =
  'border-primary text-red hover:bg-secondary flex h-10 shrink-0 cursor-pointer items-center gap-2 rounded-lg border px-4 transition-colors';

export interface AssetLibraryGroupDetailProps {
  embedded?: boolean;
  pickMode?: boolean;
  onPickMogenJob?: (input: {
    jobId: string;
    label: string;
    outputCount?: number;
  }) => void;
}

export default function AssetLibraryGroupDetail({
  embedded = false,
  pickMode = false,
  onPickMogenJob,
}: AssetLibraryGroupDetailProps) {
  const store = useStore();
  const {
    requestMoveGroupToTrash,
    requestRestoreGroupFromTrash,
    canDeleteAsset,
    restoreOwnItemTooltip,
    deleteOwnItemTooltip,
  } = useAssetLibraryAssetActions();
  const { groupDetailLoad, libraryView, selectedGroup } = store.state;
  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);

  const setScrollContainerNode = useCallback((node: HTMLDivElement | null) => {
    setScrollRoot(node);
  }, []);

  if (!selectedGroup) {
    return null;
  }

  const { items } = groupDetailLoad;
  const gridClassName = getAssetLibraryGridClassName(embedded);
  const bodyClassName = getAssetLibraryBodyClassName(embedded);
  const isInitialLoading =
    groupDetailLoad.status === 'initial' ||
    (groupDetailLoad.status === 'pending' && items.length === 0);
  const showLoadMore = groupDetailLoad.hasMore || groupDetailLoad.isLoadingMore;
  const doneLoadingAll =
    groupDetailLoad.status === 'ready' && !groupDetailLoad.hasMore;

  const isTrashView = libraryView === 'trash';
  const groupAsset = selectedGroup
    ? findAssetForGroupID(selectedGroup.groupID, store.state)
    : undefined;
  const ownershipActionDisabled = !canDeleteAsset(groupAsset);
  const ownershipActionTooltip = isTrashView
    ? restoreOwnItemTooltip
    : deleteOwnItemTooltip;

  return (
    <div ref={setScrollContainerNode} className={bodyClassName}>
      <div className="flex min-w-0 items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3">
          <button
            type="button"
            aria-label="Back to library"
            className="bg-secondary hover:bg-gray-30 dark:hover:bg-gray-60 flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-lg transition-colors"
            onClick={() => store.closeGroupDetail()}
          >
            <ChevronLeftIcon className="text-primary size-5" />
          </button>
          <Text
            as="h2"
            token="display/16/medium"
            leading="headline"
            className="text-primary min-w-0 truncate text-pretty"
            title={selectedGroup.groupName}
          >
            {selectedGroup.groupName}
          </Text>
        </div>
        {!embedded && !pickMode ? (
          <AssetLibraryBlockedActionTooltip
            disabled={ownershipActionDisabled}
            side="right"
            text={ownershipActionTooltip}
          >
            <button
              type="button"
              disabled={ownershipActionDisabled}
              className={`${GROUP_MOVE_TO_TRASH_BUTTON_CLASS} ${
                ownershipActionDisabled ? 'cursor-not-allowed opacity-50' : ''
              }`}
              onClick={() => {
                if (ownershipActionDisabled) {
                  return;
                }

                if (isTrashView) {
                  void requestRestoreGroupFromTrash();
                  return;
                }

                requestMoveGroupToTrash();
              }}
            >
              <Text token="14/medium">
                {isTrashView ? 'Move to Library' : 'Move to trash'}
              </Text>
              {isTrashView ? (
                <LearnIcon className="text-primary size-5 shrink-0 -translate-y-0.5" />
              ) : (
                <TrashIcon className="size-5" />
              )}
            </button>
          </AssetLibraryBlockedActionTooltip>
        ) : null}
      </div>

      {isInitialLoading ? (
        <AssetLibraryLoading
          embedded={embedded}
          gridClassName={gridClassName}
        />
      ) : groupDetailLoad.status === 'error' && items.length === 0 ? (
        <div className="flex flex-1 items-center justify-center p-8">
          <Text
            token="14/medium"
            className="text-secondary text-pretty text-center"
          >
            {groupDetailLoad.error ?? 'Unable to load batch items.'}
          </Text>
        </div>
      ) : (
        <>
          <ul className={gridClassName}>
            {items.map((item, index) => (
              <li key={item.id}>
                <AssetLibraryGroupItemCard
                  item={item}
                  index={index}
                  groupSelection={selectedGroup}
                  onClick={
                    pickMode && onPickMogenJob && !isTrashView
                      ? () => {
                          onPickMogenJob({
                            jobId: item.id,
                            label: item.name,
                            outputCount: item.outputCount,
                          });
                        }
                      : isTrashView
                        ? undefined
                        : () => {
                            const isBookmarked = isGroupBookmarked(
                              selectedGroup.groupID,
                              getBookmarkReferenceAssets(store.state),
                            );

                            store.openJobPreview({
                              jobId: item.id,
                              groupID: selectedGroup.groupID,
                              assetType: selectedGroup.assetType,
                              groupKind: selectedGroup.groupKind,
                              isBookmarked,
                              showMainLibraryPreviewActions:
                                isMainLibraryPageItem(embedded, libraryView),
                            });
                          }
                  }
                />
              </li>
            ))}
          </ul>
          {showLoadMore ? (
            <div className="flex justify-center py-6">
              <InfiniteLoadingTrigger
                loading={groupDetailLoad.isLoadingMore}
                done={doneLoadingAll}
                onEnter={() => void store.loadMoreGroupDetail()}
                root={embedded ? scrollRoot : undefined}
                rootMargin={ASSET_LIBRARY_LOAD_MORE_ROOT_MARGIN}
              />
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
